import type { FC, ReactNode } from 'react'

import { ArrowLeft, FlaskConical, MoveHorizontal, Sprout } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button-link'
import { Eyebrow } from '@/components/generals/section-heading'
import { ExportGlobe } from '@/components/three/export-globe'
import { MountainParallax } from '@/components/landing/mountain-parallax'

const HERO_FACTS = [
    { value: "۱۰۰٪", label: "گیاهی و طبیعی" },
    { value: "هر بچ", label: "آنالیز آزمایشگاهی" },
    { value: "۶ منطقه", label: "مقصد صادرات" }
]

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative overflow-hidden bg-forest-950 text-cream'>
            <div aria-hidden='true' className='bg-girih pointer-events-none absolute inset-0 opacity-[0.06]' />
            <div aria-hidden='true' className='pointer-events-none absolute -top-56 start-1/3 size-[44rem] rounded-full bg-gold/10 blur-3xl' />
            <div aria-hidden='true' className='pointer-events-none absolute -bottom-40 end-0 size-[40rem] rounded-full bg-forest-600/25 blur-3xl' />

            <MountainParallax />

            <div className='relative mx-auto grid max-w-7xl items-center gap-10 px-5 pb-40 pt-12 sm:px-8 sm:pb-48 lg:grid-cols-12 lg:gap-6 lg:pb-56 lg:pt-14'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 lg:col-span-6'>
                    <Eyebrow tone='dark'>
                        داروهای گیاهی کوهستان زاگرس
                    </Eyebrow>

                    <h1 id='hero-title' className='mt-6 text-[2.75rem] font-bold leading-[1.22] sm:text-6xl lg:text-[4.4rem]'>
                        از دل کوه‌های{' '}
                        <span className='text-gold-shine'>زاگرس</span>،
                        <br className='hidden sm:block' /> تا هر گوشه از جهان
                    </h1>

                    <p className='mt-6 max-w-xl text-base leading-8 text-cream/75 sm:text-lg sm:leading-9'>
                        گیاهان دارویی خودرو را در فصل اوج مواد مؤثره از ارتفاعات زاگرس برداشت می‌کنیم، هر محموله را در آزمایشگاه آنالیز و سپس بسته‌بندی می‌کنیم و با مدارک کامل به کشورهای مختلف می‌فرستیم.
                    </p>

                    <div className='mt-9 flex flex-wrap gap-3'>
                        <ButtonLink href='/products' variant='gold' size='lg'>
                            مشاهده محصولات
                            <ArrowLeft aria-hidden='true' />
                        </ButtonLink>

                        <ButtonLink href='/contact' variant='outline-light' size='lg'>
                            درخواست استعلام صادراتی
                        </ButtonLink>
                    </div>

                    <dl className='mt-12 grid max-w-xl grid-cols-3 gap-4 border-t border-gold/15 pt-8'>
                        {HERO_FACTS.map(fact => (
                            <div key={fact.label} className='flex flex-col-reverse gap-1'>
                                <dt className='text-xs text-cream/60 sm:text-sm'>
                                    {fact.label}
                                </dt>

                                <dd className='font-heading text-2xl font-bold text-gold sm:text-[2rem]'>
                                    {fact.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='relative lg:col-span-6'>
                    <div className='relative mx-auto w-full max-w-[34rem] animate-in fade-in zoom-in-95 duration-1000'>
                        <div aria-hidden='true' className='pointer-events-none absolute inset-[4%] rounded-full border border-gold/15' />
                        <div aria-hidden='true' className='pointer-events-none absolute -inset-[3%] rounded-full border border-dashed border-gold/15 motion-safe:animate-[spin_140s_linear_infinite]' />

                        <ExportGlobe />

                        <div className='pointer-events-none absolute -start-2 top-[14%] hidden items-center gap-3 rounded-2xl border border-gold/25 bg-forest-900/85 px-4 py-3 shadow-2xl backdrop-blur sm:flex'>
                            <span className='flex size-10 items-center justify-center rounded-full bg-gold/15 text-gold'>
                                <Sprout aria-hidden='true' className='size-5' />
                            </span>

                            <span className='flex flex-col'>
                                <span className='text-sm font-semibold'>برداشت در فصل گلدهی</span>
                                <span className='text-xs text-cream/60'>اوج مواد مؤثره گیاه</span>
                            </span>
                        </div>

                        <div className='pointer-events-none absolute -end-2 bottom-[12%] hidden items-center gap-3 rounded-2xl border border-gold/25 bg-forest-900/85 px-4 py-3 shadow-2xl backdrop-blur sm:flex'>
                            <span className='flex size-10 items-center justify-center rounded-full bg-gold/15 text-gold'>
                                <FlaskConical aria-hidden='true' className='size-5' />
                            </span>

                            <span className='flex flex-col'>
                                <span className='text-sm font-semibold'>برگه آنالیز هر بچ</span>
                                <span className='text-xs text-cream/60'>همراه محموله صادراتی</span>
                            </span>
                        </div>
                    </div>

                    <p className='mt-2 flex items-center justify-center gap-2 text-xs text-cream/50'>
                        <MoveHorizontal aria-hidden='true' className='size-4 text-gold/70' />
                        برای چرخاندن کره، آن را بکشید
                    </p>
                </div>
            </div>
        </section>
    )
}
